import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import {
  DeleteOutlineRounded,
  FilterListRounded,
  VisibilityRounded,
  EditRounded,
  Group,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import "./TeamTable.css";
import Image from "../../images/Sample_User_Icon_grey.png";
import BaseTable from "./BaseTable";
import FirebaseImage from "../media/FirebaseImage";
import TableSearch from "../filters/TableSearch";
import { collection, onSnapshot } from "firebase/firestore";
import { deleteDocument, firestore, setDocument } from "../../firebase";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";
import { useSelector } from "react-redux";

const styleForIconButton = {
  width: "2.3rem",
  height: "2.3rem",
  borderRadius: "2.3rem",
  color: "#01365B",
};

export default function GroupsTable({
  group,
  company,
  showForm,
  colours,
  materials,
  setValues,
}) {
  const [codes, setCodes] = React.useState([]);
  const [showModal, setShowModal] = React.useState(false);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [archived, setArchived] = React.useState(false);
  const user = useSelector((state) => state.user);
  const codesRef = collection(
    firestore,
    "Companies/" + company.id + "/groups/" + group.id + "/codes"
  );

  // Get realtime data
  function getData(collectionRef, setData) {
    return onSnapshot(
      collectionRef,
      (querySnapshot) => {
        const items = [];
        querySnapshot.forEach((doc) => {
          items.push(doc.data());
        });
        setData(items);
      },
      (error) => {
        // TODO add error handling in here
        console.log(error.message);
      }
    );
  }

  React.useEffect(() => {
    let unsubscribe = getData(codesRef, setCodes);
    return () => unsubscribe();
  }, []);

  const cells = [
    {
      id: "area",
      numeric: false,
      disablePadding: true,
      label: "Area",
      cell: (member) => (
        <TableCell
          key={member.id + "_area"}
          align="center"
          className="table-cell"
        >
          {member.area &&
            member.area.charAt(0).toUpperCase() + member.area.slice(1)}
        </TableCell>
      ),
    },
    {
      id: "code",
      numeric: false,
      disablePadding: true,
      label: "Code",
      cell: (member) => (
        <TableCell
          key={member.id + "_code"}
          align="center"
          className="table-cell"
        >
          {member.code || ""}
        </TableCell>
      ),
    },
    {
      id: "items",
      numeric: true,
      disablePadding: false,
      label: "Colours/Materials",
      cell: (member) => (
        <TableCell
          key={member.id + "_coloursMaterials"}
          align="center"
          className="table-cell"
        >
          {member.items
            ? member.items.map((item) => {
                return (
                  colours.concat(materials).find((colour) => {
                    return colour.id == item;
                  }) &&
                  colours.concat(materials).find((colour) => {
                    return colour.id == item;
                  }).name + ", "
                );
              })
            : ""}
        </TableCell>
      ),
    },
    {
      id: "products",
      numeric: true,
      disablePadding: false,
      label: "# Products",
      cell: (member) => (
        <TableCell
          key={member.id + "_products"}
          align="center"
          className="table-cell"
        >
          0
        </TableCell>
      ),
    },

    {
      id: "edit",
      numeric: true,
      disablePadding: false,
      label: "Edit",
      cell: (member) => (
        <TableCell
          key={member.email + "_edit"}
          align="center"
          className="table-cell"
        >
          <IconButton
            onClick={() => {
              setValues(member);
              showForm(group);
            }}
            style={styleForIconButton}
          >
            <EditRounded />
          </IconButton>
        </TableCell>
      ),
    },
  ];

  async function deleteCode() {
    if(["company_admin", "actuality_admin"].includes(user.role)){
      for (let index = 0; index < selectedCodes.length; index++) {
        const code = selectedCodes[index];
        if (archived) {
          await deleteDocument(
            "Companies/" + company.id + "/groups/" + group.id + "/codes/" + code
          ).then((result) => {
            console.log(result);
          });
        } else {
          let codesDoc = codes.find((co) => co.id == code);
          codesDoc.archived = true;
          await setDocument(
            "Companies/" + company.id + "/groups/" + group.id + "/codes/" + code,
            codesDoc
          ).then((result) => {
            console.log(result);
          });
        }
      }
      setShowModal(false);
      setSelectedCodes([])
    } else {
      window.alert("Only Admins can delete or archive items")
    }
  }

  async function unarchiveFunc(codeList) {
    if(["actuality_admin", "company_admin"].includes(user.role)){
      for (let index = 0; index < codeList.length; index++) {
        const code = codeList[index];
        let codesDoc = codes.find((co) => co.id == code);
        codesDoc.archived = false;
        await setDocument(
          "Companies/" + company.id + "/groups/" + group.id + "/codes/" + code,
          codesDoc
        ).then((result) => {
          console.log(result);
        });
      }
      setSelectedCodes([])
    } else {
      window.alert("Only Admins can delete or archive items")
    }
  }

  return (
    <div style={{ marginBottom: "50px" }}>
      {/* TABLE DISPLAYS ARCHIVED MEMBERS */}
      <div style={{ display: !archived ? "none" : "block" }}>
        <BaseTable
          title={
            <div style={{ display: "flex" }}>
              <div
                style={{ flex: "1", paddingLeft: "50px", paddingTop: "14px" }}
              >
                {"Archived " + group.type.charAt(0).toUpperCase() + group.type.slice(1) + " Codes"}
              </div>
              <div
                style={{
                  width: "150px",
                  height: "50px",
                  paddingTop: "0.5575rem",
                }}
              >
                <button
                  className="add-company-button"
                  onClick={() => showForm(group)}
                  style={{ width: "150px" }}
                >
                  Create Code
                </button>
              </div>
            </div>
          }
          rows={codes.filter((code) => code.archived)}
          columns={cells}
          defaultOrder={"name"}
          deleteFunc={(value) => {
            setShowModal(true);
            setSelectedCodes(value);
          }}
          unarchiveFunc={(value) => unarchiveFunc(value)}
          archived={archived}
          selectedItems={selectedCodes}
        ></BaseTable>
      </div>
      {/* TABLE DISPLAYS non-ARCHIVED MEMBERS */}
      <div style={{ display: archived ? "none" : "block" }}>
        <BaseTable
          title={
            <div style={{ display: "flex" }}>
              <div
                style={{ flex: "1", paddingLeft: "50px", paddingTop: "14px" }}
              >
                {group.type.charAt(0).toUpperCase() + group.type.slice(1) + " Codes"}
              </div>
              <div
                style={{
                  width: "150px",
                  height: "50px",
                  paddingTop: "0.5575rem",
                }}
              >
                <button
                  className="add-company-button"
                  onClick={() => showForm(group)}
                  style={{ width: "150px" }}
                >
                  Create Code
                </button>
              </div>
            </div>
          }
          rows={codes.filter((code) => !code.archived)}
          columns={cells}
          defaultOrder={"name"}
          deleteFunc={(value) => {
            setShowModal(true);
            setSelectedCodes(value);
          }}
          archived={archived}
          selectedItems={selectedCodes}
        ></BaseTable>
      </div>
      <DeleteConfirmModal
        closeCallback={(value) => setShowModal(value)}
        display={showModal}
        title={archived ? "Delete Code" : "Archive Code"}
        message={
          archived
            ? "Are you sure you would like to delete this code?"
            : "Are you sure you would like to archive this code?"
        }
        confirm={deleteCode}
      />
      <div>
        <a
          style={{ fontSize: "14px", cursor: "pointer" }}
          onClick={() => setArchived(!archived)}
        >
          {archived ? "Hide Archived Codes" : "Show Archived Codes"}
        </a>
      </div>
    </div>
  );
}
